import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import Map, { Marker, Source, Layer, NavigationControl } from 'react-map-gl/maplibre';
import 'maplibre-gl/dist/maplibre-gl.css';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  Play,
  Pause,
  RotateCcw,
  Star,
  MapPin,
  Coffee,
  Utensils,
  GraduationCap,
  AlertTriangle,
  Home
} from 'lucide-react';
import StopScrapbook from './StopScrapbook';
import './TripReplayDemo.css';

// Hardcoded fictional trip: Cleveland -> YSU -> back to Cleveland.
// Each stop has a `progress` (0-100) that defines its position on the slider
// AND on the polyline. Coordinates are real lat/lng for visual realism.
const STOPS = [
  {
    id: 'start',
    progress: 0,
    title: 'Leaving Cleveland',
    city: 'Cleveland, OH',
    date: 'Sat, May 11 · 8:42 AM',
    description: 'Packed up and hitting the road. Coffee in hand, playlist queued, Cleveland skyline in the rearview.',
    photo: '/cleveland-home.png',
    coord: [-81.6944, 41.4993],
    icon: Home,
    color: '#FFD700',
    entries: [
      { type: 'photo', url: '/cleveland-home.png', caption: 'Sun coming up over the driveway. Officially on the road.' },
      { type: 'note', title: 'The plan', body: 'Cleveland → Streetsboro for breakfast → walk YSU campus → Cozy Bean for afternoon caffeine → home before dark. Tank is full, playlist is queued.' }
    ]
  },
  {
    id: 'restaurant',
    progress: 25,
    title: 'Roadside Diner',
    city: 'Akron, OH',
    date: 'Sat, May 11 · 9:35 AM',
    description: 'Pit stop at a classic roadside diner. Smashburger and fries hit the spot.',
    photo: '/roadside-diner-1.png',
    coord: [-81.5190, 41.0814],
    icon: Utensils,
    color: '#FF8C42',
    rating: 4,
    entries: [
      { type: 'photo', url: '/roadside-diner-1.png', caption: 'Neon "OPEN" sign + chrome bar stools. The works.' },
      { type: 'photo', url: '/roadside-diner-2.png', caption: 'Smashburger with crispy edges, side of skin-on fries.' },
      { type: 'note', title: 'Quick review', body: 'Smashburger was 10/10, coffee was a little weak but the waitress kept it coming. Cash-only, sat at the counter, in and out in 40 minutes.' }
    ]
  },
  {
    id: 'ysu',
    progress: 55,
    title: 'Arrived at YSU',
    city: 'Youngstown, OH',
    date: 'Sat, May 11 · 11:20 AM',
    description: 'Made it! Walked the campus at Youngstown State University. Beautiful afternoon light on the buildings.',
    photo: '/ysu.png',
    coord: [-80.6498, 41.1067],
    icon: GraduationCap,
    color: '#E63946',
    entries: [
      { type: 'photo', url: '/ysu.png', caption: 'Maag Library steps — exactly how I remember it.' },
      { type: 'note', title: 'Walking the campus', body: 'Did the full loop from Kilcawley Center down to the football stadium and back. Quiet on a Saturday but you can feel that it’s still alive. The new student union looks great.' }
    ]
  },
  {
    id: 'coffee',
    progress: 65,
    title: 'Cozy Bean Coffee',
    city: 'Youngstown, OH',
    date: 'Sat, May 11 · 1:05 PM',
    description: 'Best espresso near campus, hands down. Chill vibe, friendly barista, and the latte art was a vibe.',
    photo: '/cozy-bean.png',
    coord: [-80.6420, 41.1090],
    icon: Coffee,
    color: '#8B5E3C',
    rating: 5,
    entries: [
      { type: 'photo', url: '/cozy-bean.png', caption: 'House oat-milk latte. The rosetta was almost too pretty to drink.' },
      { type: 'note', title: '⭐⭐⭐⭐⭐ Cozy Bean', body: 'Hands-down the best espresso pull on this side of Ohio. Beans are roasted in-house, the barista talked me through the single-origin without being weird about it. Will absolutely come back next time we’re in town.' }
    ]
  },
  {
    id: 'flat',
    progress: 85,
    title: 'Flat Tire!',
    city: 'I-76 W, OH',
    date: 'Sat, May 11 · 3:48 PM',
    description: 'Picked up a nail somewhere on the way back. Pulled over, swapped on the spare. Adventure!',
    photo: '/flat-tire.png',
    coord: [-81.1500, 41.2500],
    icon: AlertTriangle,
    color: '#D62828',
    entries: [
      { type: 'photo', url: '/flat-tire.png', caption: 'Big nail straight through the tread. RIP.' },
      { type: 'note', title: 'Lessons from the shoulder', body: 'Spare was under the trunk floor (took me a sec to remember). Jack was right there. Whole thing took maybe 25 minutes and a YouTube video. Heading straight to a tire shop on Monday.' }
    ]
  },
  {
    id: 'end',
    progress: 100,
    title: 'Trip Complete',
    city: 'Cleveland, OH',
    date: 'Sat, May 11 · 5:30 PM',
    description: 'Back home. Tired, full, slightly greasier than we started — and one great day richer.',
    photo: '/cleveland-home.png',
    coord: [-81.6944, 41.4993],
    icon: Home,
    color: '#06A77D',
    entries: [
      { type: 'photo', url: '/cleveland-home.png', caption: 'Pulled into the driveway just as the light turned gold.' },
      { type: 'note', title: 'Trip summary', body: 'Total miles: 158. Best meal: that smashburger. Best moment: walking the YSU quad in the sun. Worst moment: hearing the tire go thunk-thunk-thunk on the freeway. Would do it again next weekend.' }
    ]
  }
];

// Tolerance window in % around a stop within which it counts as "active".
const ACTIVE_TOLERANCE = 3;

// Haversine distance in meters between two [lng, lat] points.
function haversineMeters(a, b) {
  const R = 6371000;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b[1] - a[1]);
  const dLng = toRad(b[0] - a[0]);
  const lat1 = toRad(a[1]);
  const lat2 = toRad(b[1]);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

// Builds a path object: list of coords + cumulative distance per coord +
// total distance + per-stop progress (%). When given just the stops, this
// produces a straight-line fallback that matches the original behaviour.
function buildStraightPath(stops) {
  const coords = stops.map(s => s.coord);
  const cumDist = [0];
  for (let i = 1; i < coords.length; i++) {
    cumDist.push(cumDist[i - 1] + haversineMeters(coords[i - 1], coords[i]));
  }
  const totalDist = cumDist[cumDist.length - 1] || 1;
  const stopProgress = {};
  stops.forEach((s, i) => { stopProgress[s.id] = (cumDist[i] / totalDist) * 100; });
  return { coords, cumDist, totalDist, stopProgress, ready: false };
}

// Sample a position along a path by progress (0–100).
function positionAtPercent(path, progress) {
  if (!path || path.coords.length < 2) return path?.coords?.[0] || [0, 0];
  if (progress <= 0) return path.coords[0];
  if (progress >= 100) return path.coords[path.coords.length - 1];
  const target = (progress / 100) * path.totalDist;
  // Binary search through cumDist
  let lo = 0;
  let hi = path.cumDist.length - 1;
  while (lo < hi - 1) {
    const mid = (lo + hi) >> 1;
    if (path.cumDist[mid] <= target) lo = mid; else hi = mid;
  }
  const segDist = path.cumDist[hi] - path.cumDist[lo];
  const t = segDist === 0 ? 0 : (target - path.cumDist[lo]) / segDist;
  const a = path.coords[lo];
  const b = path.coords[hi];
  return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
}

// Slice of the path from the start up to current progress, with an interpolated
// final point so the trail ends exactly under the vehicle.
function trailCoordsUpTo(path, progress) {
  if (!path || path.coords.length === 0) return [];
  if (progress <= 0) return [path.coords[0]];
  const target = (progress / 100) * path.totalDist;
  const result = [];
  for (let i = 0; i < path.coords.length; i++) {
    if (path.cumDist[i] <= target) {
      result.push(path.coords[i]);
    } else {
      const prev = i - 1;
      const segDist = path.cumDist[i] - path.cumDist[prev];
      const t = segDist === 0 ? 0 : (target - path.cumDist[prev]) / segDist;
      const a = path.coords[prev];
      const b = path.coords[i];
      result.push([a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t]);
      return result;
    }
  }
  return result;
}

// Hook: fetch a real-road driving route from the public OSRM demo server
// that visits all stops in order. Falls back silently (returns null) on
// error so callers can fall through to the straight-line path.
function useRoadRoute(stops) {
  const [path, setPath] = useState(null);
  useEffect(() => {
    const coordStr = stops.map(s => `${s.coord[0]},${s.coord[1]}`).join(';');
    const url = `https://router.project-osrm.org/route/v1/driving/${coordStr}?overview=full&geometries=geojson`;
    let aborted = false;
    fetch(url)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (aborted || !data || !data.routes || !data.routes[0]) return;
        const route = data.routes[0];
        const coords = route.geometry.coordinates;
        if (!coords || coords.length < 2) return;
        const cumDist = [0];
        for (let i = 1; i < coords.length; i++) {
          cumDist.push(cumDist[i - 1] + haversineMeters(coords[i - 1], coords[i]));
        }
        const totalDist = cumDist[cumDist.length - 1] || 1;
        // Each leg corresponds to a segment between consecutive input stops.
        const legCum = [0];
        (route.legs || []).forEach(leg => {
          legCum.push(legCum[legCum.length - 1] + (leg.distance || 0));
        });
        const totalLegDist = legCum[legCum.length - 1] || totalDist;
        const stopProgress = {};
        stops.forEach((s, i) => {
          stopProgress[s.id] = ((legCum[i] || 0) / totalLegDist) * 100;
        });
        setPath({ coords, cumDist, totalDist, stopProgress, ready: true });
      })
      .catch(() => { /* keep null, fall back to straight path */ });
    return () => { aborted = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return path;
}

const TripReplayDemo = () => {
  const [progress, setProgress] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [viewState, setViewState] = useState({
    longitude: -81.17,
    latitude: 41.3,
    zoom: 8.2
  });
  const animRef = useRef(null);
  const lastTickRef = useRef(null);

  // --- Sticky "magnetic" catch at each pit stop ---
  // When the slider scrubs into the zone around a stop it snaps to that stop
  // and freezes briefly, then releases so the user can continue. Each stop
  // can only catch once until the slider has moved well past it (re-arm).
  const SNAP_THRESHOLD = 0.8;   // % within stop progress that triggers a catch
  const HOLD_MS = 160;          // how long the catch holds before releasing
  const REARM_DISTANCE = 2.5;   // % away from stop required to re-arm it

  // Currently-open stop scrapbook modal (null when closed).
  const [scrapbookStop, setScrapbookStop] = useState(null);

  const [stickyStopId, setStickyStopId] = useState(null);
  const stickyStopIdRef = useRef(null);
  const stickyTimerRef = useRef(null);
  const armedStopsRef = useRef(new Set(STOPS.map(s => s.id)));

  const setSticky = (id) => {
    stickyStopIdRef.current = id;
    setStickyStopId(id);
  };

  const clearSticky = () => {
    if (stickyTimerRef.current) {
      clearTimeout(stickyTimerRef.current);
      stickyTimerRef.current = null;
    }
    setSticky(null);
  };

  // Cleanup timer on unmount.
  useEffect(() => () => {
    if (stickyTimerRef.current) clearTimeout(stickyTimerRef.current);
  }, []);

  // Real-road route via OSRM; falls back to straight-line until the fetch
  // resolves (or forever if the call fails).
  const roadPath = useRoadRoute(STOPS);
  const path = useMemo(
    () => roadPath || buildStraightPath(STOPS),
    [roadPath]
  );

  // Stops with their progress remapped to match the loaded path. Used by all
  // ticks/markers/sticky logic so they line up with the real-road geometry.
  const effectiveStops = useMemo(
    () => STOPS.map(s => ({ ...s, progress: path.stopProgress[s.id] ?? s.progress })),
    [path]
  );
  const stopsRef = useRef(effectiveStops);
  useEffect(() => { stopsRef.current = effectiveStops; }, [effectiveStops]);

  // Central progress updater. When `force` is true (explicit jumps), bypass
  // the catch and treat the target stop as already consumed so we don't
  // immediately re-catch it.
  const tryAdvance = useCallback((next, opts = {}) => {
    const { force = false } = opts;
    const clamped = Math.max(0, Math.min(100, next));

    if (force) {
      clearSticky();
      // Disarm any stop we're landing exactly on; re-arm others as appropriate.
      for (const s of stopsRef.current) {
        if (Math.abs(clamped - s.progress) <= SNAP_THRESHOLD) {
          armedStopsRef.current.delete(s.id);
        } else if (Math.abs(clamped - s.progress) > REARM_DISTANCE) {
          armedStopsRef.current.add(s.id);
        }
      }
      setProgress(clamped);
      return;
    }

    // While locked, ignore any updates.
    if (stickyStopIdRef.current) return;

    // Re-arm stops the slider has moved well clear of.
    for (const s of stopsRef.current) {
      if (!armedStopsRef.current.has(s.id) && Math.abs(clamped - s.progress) > REARM_DISTANCE) {
        armedStopsRef.current.add(s.id);
      }
    }

    // Check armed stops for a catch.
    for (const s of stopsRef.current) {
      if (!armedStopsRef.current.has(s.id)) continue;
      if (Math.abs(clamped - s.progress) <= SNAP_THRESHOLD) {
        armedStopsRef.current.delete(s.id);
        setSticky(s.id);
        setProgress(s.progress);
        stickyTimerRef.current = setTimeout(() => {
          stickyStopIdRef.current = null;
          setStickyStopId(null);
          stickyTimerRef.current = null;
        }, HOLD_MS);
        return;
      }
    }

    setProgress(clamped);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [vehicleSrc, setVehicleSrc] = useState('/subaru-badge.png');

  // Vehicle heading. The source image faces LEFT with wheels down. To avoid
  // the car ever appearing upside down, we mirror the image horizontally when
  // travel goes east instead of rotating past vertical. Rotation is bounded
  // to roughly ±90° (the slope of the road), keeping the wheels under the car.
  // We only update heading on forward motion so backward scrubbing visually
  // looks like driving in reverse.
  const headingFromDelta = (dlng, dlat) => {
    // Screen coords: dx = dlng (east-right), dy = -dlat (north is screen-up).
    const dx = dlng;
    const dy = -dlat;
    if (dx >= 0) {
      // East-bound: mirror image, rotate by angle measured from east axis.
      return { angle: (Math.atan2(dy, dx) * 180) / Math.PI, facingRight: true };
    }
    // West-bound: keep original (left-facing), rotate so the slope is correct.
    return { angle: (Math.atan2(-dy, -dx) * 180) / Math.PI, facingRight: false };
  };

  const [heading, setHeading] = useState(() => {
    const a = STOPS[0].coord;
    const b = STOPS[1].coord;
    return headingFromDelta(b[0] - a[0], b[1] - a[1]);
  });
  const lastProgressRef = useRef(0);

  useEffect(() => {
    const prev = lastProgressRef.current;
    // Only recompute heading on meaningful forward motion.
    if (progress > prev + 0.05) {
      const prevPos = positionAtPercent(path, prev);
      const currPos = positionAtPercent(path, progress);
      const dlng = currPos[0] - prevPos[0];
      const dlat = currPos[1] - prevPos[1];
      if (dlng !== 0 || dlat !== 0) {
        setHeading(headingFromDelta(dlng, dlat));
      }
    }
    lastProgressRef.current = progress;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [progress, path]);

  // Runtime "background remover": strips near-white pixels from the badge so
  // it floats on the map with no white box around it. Falls back to the raw
  // image if anything goes wrong (e.g. CORS).
  useEffect(() => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        const data = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const px = data.data;
        const THRESHOLD = 235; // r,g,b all >= this -> treat as background
        for (let i = 0; i < px.length; i += 4) {
          const r = px[i], g = px[i + 1], b = px[i + 2];
          if (r >= THRESHOLD && g >= THRESHOLD && b >= THRESHOLD) {
            px[i + 3] = 0; // transparent
          } else {
            // Soft edge: fade pixels that are near-white to reduce halo
            const minC = Math.min(r, g, b);
            if (minC > 200) {
              const fade = (minC - 200) / (THRESHOLD - 200);
              px[i + 3] = Math.round(px[i + 3] * (1 - fade));
            }
          }
        }
        ctx.putImageData(data, 0, 0);
        setVehicleSrc(canvas.toDataURL('image/png'));
      } catch (err) {
        // ignore — keep original src
      }
    };
    img.src = '/subaru-badge.png';
  }, []);

  const vehiclePos = useMemo(() => positionAtPercent(path, progress), [path, progress]);
  const trailCoords = useMemo(() => trailCoordsUpTo(path, progress), [path, progress]);
  const activeStop = useMemo(() => {
    let best = null;
    let bestDist = Infinity;
    for (const s of effectiveStops) {
      const d = Math.abs(s.progress - progress);
      if (d <= ACTIVE_TOLERANCE && d < bestDist) {
        best = s;
        bestDist = d;
      }
    }
    return best;
  }, [effectiveStops, progress]);

  const trailGeoJSON = useMemo(() => ({
    type: 'Feature',
    geometry: { type: 'LineString', coordinates: trailCoords },
    properties: {}
  }), [trailCoords]);

  const fullRouteGeoJSON = useMemo(() => ({
    type: 'Feature',
    geometry: { type: 'LineString', coordinates: path.coords },
    properties: {}
  }), [path]);

  // Play / pause animation. Completes a full trip in ~18 seconds.
  useEffect(() => {
    if (!isPlaying) {
      lastTickRef.current = null;
      cancelAnimationFrame(animRef.current);
      return;
    }
    const TRIP_DURATION_MS = 18000;
    const step = (ts) => {
      if (lastTickRef.current == null) lastTickRef.current = ts;
      const dt = ts - lastTickRef.current;
      lastTickRef.current = ts;
      // Skip advancing while a sticky catch is active so playback pauses
      // briefly at each pit stop too.
      if (!stickyStopIdRef.current) {
        setProgress(prev => {
          const target = prev + (dt / TRIP_DURATION_MS) * 100;
          if (target >= 100) {
            setIsPlaying(false);
            return 100;
          }
          // Inline catch check — same logic as tryAdvance but inside the
          // functional updater so we always work off the latest value.
          for (const s of stopsRef.current) {
            if (!armedStopsRef.current.has(s.id)) continue;
            if (Math.abs(target - s.progress) <= SNAP_THRESHOLD) {
              armedStopsRef.current.delete(s.id);
              setSticky(s.id);
              stickyTimerRef.current = setTimeout(() => {
                stickyStopIdRef.current = null;
                setStickyStopId(null);
                stickyTimerRef.current = null;
              }, HOLD_MS);
              return s.progress;
            }
            if (Math.abs(target - s.progress) > REARM_DISTANCE) {
              armedStopsRef.current.add(s.id);
            }
          }
          return target;
        });
      }
      animRef.current = requestAnimationFrame(step);
    };
    animRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(animRef.current);
  }, [isPlaying]);

  const handlePlayPause = () => {
    if (progress >= 100) {
      // Reset and re-arm everything for a fresh playthrough.
      armedStopsRef.current = new Set(STOPS.map(s => s.id));
      tryAdvance(0, { force: true });
      setIsPlaying(true);
    } else {
      setIsPlaying(p => !p);
    }
  };

  const handleReset = () => {
    setIsPlaying(false);
    armedStopsRef.current = new Set(STOPS.map(s => s.id));
    tryAdvance(0, { force: true });
  };

  const goToStop = useCallback((stop) => {
    setIsPlaying(false);
    tryAdvance(stop.progress, { force: true });
    // Open the mini-scrapbook modal for this stop.
    setScrapbookStop(stop);
  }, [tryAdvance]);

  // Gently follow vehicle when playing (don't fight user pan otherwise).
  useEffect(() => {
    if (!isPlaying) return;
    setViewState(vs => ({
      ...vs,
      longitude: vehiclePos[0],
      latitude: vehiclePos[1],
      transitionDuration: 200
    }));
  }, [vehiclePos, isPlaying]);

  return (
    <div className="replay-demo">
      <header className="replay-header">
        <Link to="/" className="replay-back-btn">
          <ArrowLeft size={18} /> Back
        </Link>
        <div className="replay-title">
          <h1>Trip Replay</h1>
          <p>Cleveland → YSU → Home · drag the slider to time-travel</p>
        </div>
        <div className="replay-progress-readout">
          {Math.round(progress)}%
        </div>
      </header>

      <div className="replay-body">
        {/* MAP */}
        <div className="replay-map-wrap">
          <Map
            {...viewState}
            onMove={evt => setViewState(evt.viewState)}
            style={{ width: '100%', height: '100%' }}
            mapStyle="https://tiles.openfreemap.org/styles/bright"
            attributionControl={false}
          >
            <NavigationControl position="top-right" />

            {/* Faint full route */}
            <Source id="full-route" type="geojson" data={fullRouteGeoJSON}>
              <Layer
                id="full-route-layer"
                type="line"
                paint={{
                  'line-color': '#9ec5ff',
                  'line-width': 4,
                  'line-opacity': 0.45,
                  'line-dasharray': [2, 2]
                }}
              />
            </Source>

            {/* Active blue trail */}
            <Source id="trail" type="geojson" data={trailGeoJSON}>
              <Layer
                id="trail-layer"
                type="line"
                paint={{
                  'line-color': '#1d6bff',
                  'line-width': 6,
                  'line-opacity': 0.95
                }}
                layout={{
                  'line-cap': 'round',
                  'line-join': 'round'
                }}
              />
            </Source>

            {/* Stop pins */}
            {effectiveStops.map(stop => {
              const Icon = stop.icon;
              const isActive = activeStop?.id === stop.id;
              const isReached = progress >= stop.progress;
              return (
                <Marker
                  key={stop.id}
                  longitude={stop.coord[0]}
                  latitude={stop.coord[1]}
                  anchor="bottom"
                  onClick={(e) => {
                    e.originalEvent.stopPropagation();
                    goToStop(stop);
                  }}
                >
                  <div
                    className={`replay-pin ${isActive ? 'active' : ''} ${isReached ? 'reached' : 'pending'}`}
                    style={{ '--pin-color': stop.color }}
                    title={stop.title}
                  >
                    <div className="replay-pin-bubble">
                      <Icon size={18} />
                    </div>
                    <div className="replay-pin-tip" />
                  </div>
                </Marker>
              );
            })}

            {/* Vehicle marker */}
            <Marker longitude={vehiclePos[0]} latitude={vehiclePos[1]} anchor="center">
              <div className="replay-vehicle">
                <img
                  src={vehicleSrc}
                  alt="Vehicle"
                  className="replay-vehicle-img"
                  style={{
                    transform: `rotate(${heading.angle}deg) scaleX(${heading.facingRight ? -1 : 1})`,
                    transition: 'transform 0.25s ease-out'
                  }}
                />
              </div>
            </Marker>

            {/* Active stop popup card */}
            <AnimatePresence>
              {activeStop && (
                <Marker
                  key={`popup-${activeStop.id}`}
                  longitude={activeStop.coord[0]}
                  latitude={activeStop.coord[1]}
                  anchor="bottom"
                  offset={[0, -56]}
                >
                  <motion.div
                    className="replay-popup"
                    initial={{ opacity: 0, y: 8, scale: 0.92 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 8, scale: 0.92 }}
                    transition={{ duration: 0.18 }}
                    style={{ '--accent': activeStop.color }}
                  >
                    <div className="replay-popup-img-wrap">
                      <img src={activeStop.photo} alt={activeStop.title} />
                    </div>
                    <div className="replay-popup-body">
                      <div className="replay-popup-title-row">
                        <h3>{activeStop.title}</h3>
                      </div>
                      <p className="replay-popup-city">
                        <MapPin size={12} /> {activeStop.city}
                      </p>
                      <p className="replay-popup-desc">{activeStop.description}</p>
                      {activeStop.rating && (
                        <div className="replay-popup-rating">
                          {Array.from({ length: 5 }).map((_, i) => (
                            <Star
                              key={i}
                              size={14}
                              fill={i < activeStop.rating ? '#FFB400' : 'none'}
                              color={i < activeStop.rating ? '#FFB400' : '#ccc'}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  </motion.div>
                </Marker>
              )}
            </AnimatePresence>
          </Map>
        </div>

        {/* SCRAPBOOK PANEL */}
        <aside className="replay-scrapbook">
          <h2>Scrapbook</h2>
          <p className="replay-scrapbook-sub">Click an item to jump there.</p>
          <div className="replay-scrapbook-list">
            {effectiveStops.map(stop => {
              const Icon = stop.icon;
              const isActive = activeStop?.id === stop.id;
              const isReached = progress >= stop.progress;
              return (
                <button
                  key={stop.id}
                  className={`replay-scrap-item ${isActive ? 'active' : ''} ${isReached ? 'reached' : ''}`}
                  style={{ '--accent': stop.color }}
                  onClick={() => goToStop(stop)}
                >
                  <div className="replay-scrap-thumb">
                    <img src={stop.photo} alt={stop.title} />
                    <div className="replay-scrap-icon"><Icon size={14} /></div>
                  </div>
                  <div className="replay-scrap-info">
                    <div className="replay-scrap-head">
                      <span className="replay-scrap-progress">{stop.progress}%</span>
                      <h4>{stop.title}</h4>
                    </div>
                    <p>{stop.description}</p>
                    {stop.rating && (
                      <div className="replay-scrap-rating">
                        <span className="replay-scrap-rating-chip">{stop.rating}.0</span>
                        <div className="replay-scrap-rating-stars">
                          {Array.from({ length: 5 }).map((_, i) => (
                            <Star
                              key={i}
                              size={13}
                              fill={i < stop.rating ? '#FFB400' : 'none'}
                              color={i < stop.rating ? '#FFB400' : '#cbd5e1'}
                              strokeWidth={2}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </aside>
      </div>

      {/* SLIDER */}
      <div className="replay-slider-bar">
        <div className="replay-slider-controls">
          <button className="replay-ctrl-btn" onClick={handlePlayPause} title={isPlaying ? 'Pause' : 'Play'}>
            {isPlaying ? <Pause size={18} /> : <Play size={18} />}
          </button>
          <button className="replay-ctrl-btn" onClick={handleReset} title="Reset">
            <RotateCcw size={18} />
          </button>
        </div>

        <div className="replay-slider-track-wrap">
          <div className="replay-slider-track-fill" style={{ width: `${progress}%` }} />
          {effectiveStops.map(stop => {
            const Icon = stop.icon;
            const isActive = activeStop?.id === stop.id;
            const isReached = progress >= stop.progress;
            return (
              <button
                key={stop.id}
                className={`replay-slider-tick ${isActive ? 'active' : ''} ${isReached ? 'reached' : ''}`}
                style={{ left: `${stop.progress}%`, '--accent': stop.color }}
                onClick={() => goToStop(stop)}
                title={`${stop.title} (${stop.progress}%)`}
              >
                <span className="replay-slider-tick-dot">
                  <Icon size={11} />
                </span>
                <span className="replay-slider-tick-label">{stop.title}</span>
              </button>
            );
          })}
          <input
            type="range"
            className={`replay-slider-input ${stickyStopId ? 'is-stuck' : ''}`}
            min={0}
            max={100}
            step={0.1}
            value={progress}
            onChange={(e) => {
              setIsPlaying(false);
              tryAdvance(parseFloat(e.target.value));
            }}
            aria-label="Trip progress"
          />
        </div>
      </div>

      {/* Mini-scrapbook modal for the currently-clicked stop */}
      <StopScrapbook
        stop={scrapbookStop}
        onClose={() => setScrapbookStop(null)}
      />
    </div>
  );
};

export default TripReplayDemo;
